import { AlarmClock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLines } from "@/features/lines/hooks";
import type { ProductionOrderFilters } from "../../types/production-orders.types.js";
import { OP_STATUS_LABEL } from "./production-order-utils";
import { localDayEnd, localMidnight } from "./timeline-range";

const STATUSES = ["SCHEDULED", "RUNNING", "COMPLETED", "CANCELLED"] as const;
const PILL =
  "h-8 rounded-full border border-border-default bg-card px-3 text-xs text-text-secondary";

// Filtros da lista: Linha · Status · Responsável · Período, numa barra de
// superfície com controles em pílula + contagem inline (espelha o OpFilterStrip do protótipo).
export function ProductionOrderFiltersBar({
  filters,
  onChange,
  count,
}: {
  filters: ProductionOrderFilters;
  onChange: (f: ProductionOrderFilters) => void;
  count: number;
}) {
  const linesQuery = useLines();
  const { data: lines = [] } = linesQuery;
  const set = (patch: Partial<ProductionOrderFilters>) =>
    onChange({ ...filters, ...patch, offset: 0 });
  // Bounds em meia-noite LOCAL do dia escolhido (não UTC): um usuário em UTC-3 que filtra "25/06"
  // consulta o dia 25 local. `to` = último ms do dia local (mesma convenção de rangeBoundsIso).
  const dayStart = (d: string) => (d ? localMidnight(d).toISOString() : undefined);
  const dayEnd = (d: string) =>
    d ? new Date(localDayEnd(d).getTime() - 1).toISOString() : undefined;

  return (
    <div className="flex flex-1 flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2.5">
      <select
        className={PILL}
        aria-label="Linha"
        value={filters.lineId ?? ""}
        onChange={(e) => set({ lineId: e.target.value || undefined })}
        // Filtro de baixo risco: em erro, degrada para "Todas as linhas" em vez de
        // bloquear a barra inteira (Status/Responsável/Período não dependem de useLines()).
        disabled={linesQuery.isError}
      >
        <option value="">Todas as linhas</option>
        {lines.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </select>
      <select
        className={PILL}
        aria-label="Status"
        value={filters.status ?? ""}
        onChange={(e) =>
          set({ status: (e.target.value || undefined) as ProductionOrderFilters["status"] })
        }
      >
        <option value="">Todos os status</option>
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {OP_STATUS_LABEL[s]}
          </option>
        ))}
      </select>
      <input
        className={PILL}
        aria-label="Responsável"
        placeholder="Responsável"
        value={filters.responsible ?? ""}
        onChange={(e) => set({ responsible: e.target.value || undefined })}
      />
      <input
        type="date"
        className={PILL}
        aria-label="De"
        value={filters.from?.slice(0, 10) ?? ""}
        onChange={(e) => set({ from: dayStart(e.target.value) })}
      />
      <input
        type="date"
        className={PILL}
        aria-label="Até"
        value={filters.to?.slice(0, 10) ?? ""}
        onChange={(e) => set({ to: dayEnd(e.target.value) })}
      />
      {/* "somente atrasadas": toggle (não select) porque é um recorte binário de
          exceção. `aria-pressed` dá o estado a leitores de tela; o estado ligado usa o tom de
          perigo do próprio dado que ele filtra. */}
      <button
        type="button"
        aria-pressed={!!filters.lateOnly}
        onClick={() => set({ lateOnly: filters.lateOnly ? undefined : true })}
        className={cn(
          PILL,
          "inline-flex items-center gap-1.5 transition-colors",
          filters.lateOnly &&
            "border-rd-danger-500/40 bg-rd-danger-50 font-medium text-rd-danger-700"
        )}
      >
        <AlarmClock className="h-3.5 w-3.5" />
        Somente atrasadas
      </button>
      <div className="ml-auto text-xs text-text-tertiary">
        <span className="font-mono tabular-nums">{count}</span> ordens
      </div>
    </div>
  );
}
